import { ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import {
  Cell,
  HeaderCell,
  TableContainer,
  TableBody,
} from '../draggable-table/styles';
import { ColumnType } from '../draggable-table/types';

export const TableHeader = styled.div<{ $columnWidths: string[] }>`
  display: grid;
  grid-template-columns: ${({ $columnWidths }) =>
    `repeat(${$columnWidths.length}, minmax(75px, 1fr))`};
  background-color: var(--white-100);
  padding: var(--spacing-03);
  border-bottom: 1px solid var(--gray-10);
  gap: var(--spacing-03);
  width: 100%;
`;

export const TableRow = styled.div<{ $columnWidths: string[] }>`
  width: 100%;
  display: grid;
  grid-template-columns: ${({ $columnWidths }) =>
    `repeat(${$columnWidths.length}, minmax(75px, 1fr))`};
  padding: var(--spacing-03);
  border-bottom: 1px solid var(--gray-10);
  gap: var(--spacing-03);
  align-items: center;

  &:nth-child(even) {
    border-bottom: 2px solid var(--gray-10);
  }
`;

export interface TableProps<T> {
  columns: ColumnType<T>[];
  data: T[];
  onRowClick?: (record: T) => void;
  loading?: boolean;
  emptyText?: string | ReactNode;
}

export function SimpleTable<T extends { id: string | number }>({
  columns,
  data,
  onRowClick,
  loading,
  emptyText = 'No data',
}: TableProps<T>) {
  const columnWidths = useMemo(
    () => columns.map((column) => column.width ?? '1fr'),
    [columns],
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!data.length) {
    return <div>{emptyText}</div>;
  }

  return (
    <TableContainer>
      <TableHeader $columnWidths={columnWidths}>
        {columns.map((column) => (
          <HeaderCell key={column.key} style={{ width: column.width }}>
            {column.title}
          </HeaderCell>
        ))}
      </TableHeader>

      <TableBody>
        {data.map((record) => (
          <TableRow key={record.id} $columnWidths={columnWidths}>
            {columns.map((column) => (
              <Cell
                key={`${record.id}-${column.key}`}
                onClick={() => onRowClick?.(record)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {column.render
                  ? column.render(
                      record[column.key as keyof T],
                      record,
                      data.indexOf(record),
                      data,
                    )
                  : String(record[column.key as keyof T])}
              </Cell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </TableContainer>
  );
}
